import React, { useState, useEffect, useRef } from 'react';

// Canonical buyer statuses — must mirror backend/services/buyer-statuses.js.
// (CRA can't import from the backend, so we keep two copies. Update both if you rename.)
export const BUYER_STATUSES = ['new', 'working', 'onboarded', 'buyer', 'unqualified', 'blacklisted', 'unsubscribed'];

// Statuses eligible to receive marketing — used by the wizard's "All marketable" mode
// and by the badge to grey-out the inline status changer for already-marketable buyers.
export const MARKETABLE_STATUSES = ['onboarded', 'buyer'];

// Color + label for each status — warm minimalist palette.
// label   — title-case display string ("Onboarded")
// color   — text color of the pill
// bg      — translucent background of the pill
// border  — pill outline (subtle)
// desc    — what this status means, shown as tooltip + in the changer menu
export const STATUS_META = {
  new: {
    label: 'New', color: '#E0A526', bg: 'rgba(224, 165, 38, 0.12)', border: 'rgba(224, 165, 38, 0.35)',
    desc: 'Fresh lead — not yet contacted or qualified.',
  },
  working: {
    label: 'Working', color: '#B89968', bg: 'rgba(184, 153, 104, 0.14)', border: 'rgba(184, 153, 104, 0.35)',
    desc: 'In conversation — qualifying their buy box.',
  },
  onboarded: {
    label: 'Onboarded', color: '#4A7C59', bg: 'rgba(74, 124, 89, 0.12)', border: 'rgba(74, 124, 89, 0.35)',
    desc: 'Intake complete — ready to receive deals.',
  },
  buyer: {
    label: 'Buyer', color: '#14504A', bg: 'rgba(20, 80, 74, 0.14)', border: 'rgba(20, 80, 74, 0.4)',
    desc: 'Established repeat buyer.',
  },
  unqualified: {
    label: 'Unqualified', color: '#8A7969', bg: 'rgba(138, 121, 105, 0.14)', border: 'rgba(138, 121, 105, 0.35)',
    desc: 'Vetted but not a fit right now — excluded from outreach. May re-qualify later.',
  },
  blacklisted: {
    label: 'Blacklisted', color: '#B85450', bg: 'rgba(184, 84, 80, 0.12)', border: 'rgba(184, 84, 80, 0.4)',
    desc: 'Do-not-contact — excluded from all outreach.',
  },
  unsubscribed: {
    label: 'Unsubscribed', color: '#6B6760', bg: 'rgba(107, 103, 96, 0.14)', border: 'rgba(107, 103, 96, 0.35)',
    desc: 'Opted out — excluded from all outreach.',
  },
};

// Display-only badge — render the colored pill for a status.
// Pass `readOnly` to disable the click-to-change behavior.
export default function BuyerStatusBadge({ status, onChange, readOnly = false, size = 'sm' }) {
  const meta = STATUS_META[status] || STATUS_META.buyer;
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function onDoc(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const sizing = size === 'lg'
    ? { fontSize: 12, padding: '5px 12px' }
    : { fontSize: 10, padding: '3px 9px' };

  const pill = (
    <span
      title={meta.desc}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        borderRadius: 999, fontWeight: 700,
        letterSpacing: '0.04em', textTransform: 'uppercase',
        background: meta.bg, color: meta.color,
        border: `1px solid ${meta.border}`,
        cursor: readOnly ? 'default' : 'pointer',
        whiteSpace: 'nowrap',
        ...sizing,
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: meta.color, flexShrink: 0 }} />
      {meta.label}
      {!readOnly && <span style={{ fontSize: 8, opacity: 0.7, marginLeft: 1 }}>▾</span>}
    </span>
  );

  if (readOnly || !onChange) return pill;

  return (
    <span ref={wrapRef} style={{ position: 'relative', display: 'inline-block' }}
          onClick={e => { e.stopPropagation(); setOpen(o => !o); }}>
      {pill}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 100,
          background: 'var(--navy-card)', border: '1px solid var(--navy-border)',
          borderRadius: 8, padding: 4, boxShadow: '0 6px 20px rgba(26,23,20,0.12)',
          width: 220,
        }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '4px 8px 6px' }}>
            Change status to…
          </div>
          {BUYER_STATUSES.map(s => {
            const m = STATUS_META[s];
            const active = s === status;
            return (
              <div
                key={s}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  if (!active && onChange) onChange(s);
                }}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                  padding: '7px 8px', borderRadius: 6,
                  cursor: active ? 'default' : 'pointer',
                  background: active ? 'var(--navy)' : 'transparent',
                }}
                onMouseOver={e => !active && (e.currentTarget.style.background = 'var(--navy)')}
                onMouseOut={e => !active && (e.currentTarget.style.background = 'transparent')}
              >
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: m.color, marginTop: 5, flexShrink: 0 }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: m.color }}>
                    {m.label}{active && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--gray-500)' }}>· current</span>}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 1, lineHeight: 1.4 }}>{m.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </span>
  );
}
